/**
 * File globals.php
 * Contains global functions and variables for use across multiple js files
 */

const globalCookieDomain = getCookieDomain();
const globalLowerSearchParams = getLowerSearchParams();
const genericEventName = "Lead Generation Interaction Captured";

function getPath() {
  return window.location.pathname;
}

function getMemberSubdomain() {
  return getSubdomain().replace('wp', 'member');
}

function getMemberDomain() {
  let memberSubdomain = getMemberSubdomain();

  return isPreProd()
    ? 'https://' + memberSubdomain + '.chime.com'
    : 'https://member.chime.com';
}

function getEnrollLink() {
  return getMemberDomain() + '/enroll/';
}

function getCookieDomain() {
  return isLocal() ? getDomain() : getDomain().replace(/.+?\./, '.');
}

function isMobile() {
  return (window.innerWidth || document.documentElement.clientWidth) <= 859;
}

function isDesktop() {
  return (window.innerWidth || document.documentElement.clientWidth) >= 1100;
}

function getReferralId() {
  var path = getPath();
  return (path.indexOf('/r/') !== -1) ? 'raf' :
    (path.indexOf('/partners/') !== -1) ? 'p' :
      '';
}

function getLowerSearchParams() {
  const newParams = new URLSearchParams();
  for (const [name, value] of getSearchParams()) {
    newParams.append(name.toLowerCase(), value);
  }
  return newParams;
}

function checkParamsExist(params) {
  const paramsExist = [];
  for (let i = 0, len = params.length; i < len; i++) {
    const param = params[i];
    paramsExist.push(globalSearchParams.has(param));
  }
  return paramsExist;
}

function buildParamProps(params) {
  const properties = {};
  for (let i = 0, len = params.length; i < len; i++) {
    const param = params[i];
    properties[param] = globalSearchParams.get(param);
  }
  return properties;
}

function checkParamValue(param, value) {
  return (globalSearchParams.get(param) === value);
}

function redirect(url) {
  return () => { window.location.href = url };
}

function removeClassFromAll(c) {
  const els = document.querySelectorAll('.' + c);
  if (els.length > 0) {
    for (let i = 0; i < els.length; i++) {
      els[i].classList.remove(c);
    }
  }
}

function waitForEl(selector, callback) {
  if (document.querySelectorAll(selector).length) {
    callback();
  } else {
    setTimeout(function () {
      waitForEl(selector, callback);
    }, 100);
  }
}

function validateEmail(email) {
  const pattern = /^([a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+(\.[a-z\d!#$%&'*+\-\/=?^_`{|}~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]+)*|"((([ \t]*\r\n)?[ \t]+)?([\x01-\x08\x0b\x0c\x0e-\x1f\x7f\x21\x23-\x5b\x5d-\x7e\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|\\[\x01-\x09\x0b\x0c\x0d-\x7f\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))*(([ \t]*\r\n)?[ \t]+)?")@(([a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\d\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.)+([a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF][a-z\d\-._~\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]*[a-z\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])\.?$/i;
  return pattern.test(email);
} 

/* Analytics SDK */
// SEE https://github.com/1debit/chime-frontend/blob/main/packages/1debit-analytics/README.md
// For extracting module data for Analytics SDK
function extractModuleData(attributes) {
  const moduleKeys = new Set(["module_title", "module_index", "module_type"]);
  const extractedData = Object.fromEntries(
    attributes
      .filter(attr => moduleKeys.has(attr.key))
      .map(attr => [
        attr.key,
        attr.key === "module_index" ? String(attr.value) : attr.value // stringify the index #
      ])
  );
  return {
    module_title: "unknown",
    module_index: "unknown",
    module_type: "unknown",
    ...extractedData
  };
}
/* End Analytics SDK */

function segmentTrackAndReturn(eventName, properties = {}) {
  if (typeof analytics !== 'undefined') {
    segment(
      'track',
      eventName,
      properties
    );
    return true;
  }
  return false;
}

/**
 * Function to send new generic segment event
 * @param {LeadGenerationInteractionCaptured} properties 
 */
async function segmentGenericEvent(
  eventType,
  eventProperties = {},
  callback = () => { },
  shouldSendToFacebook = false,
  excludeAdditionalProperties = false
) {

  segment(
    eventType,
    genericEventName,
    eventProperties,
    callback,
    shouldSendToFacebook,
    excludeAdditionalProperties
  );
}

/*
 * Generic AJAX post function
 *
 * @param {Object}    ajaxArgs
 * @param {string}    ajaxArgs.url            The URL to send the AJAX request to
 * @param {Object}    ajaxArgs.data           The data to send in the AJAX request
 * @param {function}  ajaxArgs.successFunc    The function to call if the AJAX request is successful
 * @param {function}  ajaxArgs.errorFunc      The function to call if the AJAX request fails
 * @param {function}  ajaxArgs.completeFunc   The function to call after the AJAX request is completed
 * 
 * @returns {void}
 */
function sendAjaxPost(ajaxArgs) {
  const { url, data, successFunc, errorFunc, completeFunc } = ajaxArgs;

  if (!jQuery || !url) {
    errorFunc(false, 'error', 'Client side error');
    return;
  }

  const emptyFunc = () => {};
  
  jQuery.ajax({
    url: url,
    type: 'post',
    data: data,
    success: successFunc ?? emptyFunc,
    error: errorFunc ?? emptyFunc,
    complete: completeFunc ?? emptyFunc,
  });
}

/**
 * Returns the ExPlat url based on the environment
 * 
 * @returns {string}
 */
function getApiUrl() {
  const ENV = window.getEnv();
  switch (ENV) {
    case 'local':
      return 'https://api-v2-dev1.chimebank.com/graphql';
      // return 'https://api.chm.life/graphql'; // specific to using Halo
    case 'prod':
      return 'https://api-v2.chimebank.com/graphql';
    default:
      return `https://api-v2-${ENV}.chimebank.com/graphql`;
  }
};


const sscmsFetchGraphQL = async (query) => {
  const options = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'App-Version': '9999.9999.9999',
      'Chime-Client-Platform': 'web',
      'Chime-Client-Version': '9999.9999.9999',
      'Chime-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    body: JSON.stringify({ query, variables: {} })
  };

  try {
    const response = await fetch(getApiUrl(), options);
    if (!response.ok) {
      console.log(`ERROR: sscmsFetchGraphQL failed: ${response.status}`);
      return {};
    }
    const json = await response.json();
    return json;
  } catch (err) {
    console.log(err, 'ERROR: sscmsFetchGraphQL failed');
    return {};
  }
};


function getIncentiveTtlValue(value) {
  try {
    return atob(value);
  } catch (e) {
    return 0;
  }
}


function setReferrerCookie(name, value, type, expires = null, referrerName = null, incentiveFields = null) {

  // Create expiration date
  if (!expires) {
    const date = new Date;
    date.setDate(date.getDate() + 30);
    expires = date;
  }

  // Set cookie for referral_token
  const rtCookieValue = (name == 'referral_token') ? encodeURIComponent(`{"${name}":"${value}","referred_by_type":"${type}"}`) : value;
  document.cookie = name + "=" + rtCookieValue + ";domain=" + globalCookieDomain + ";path=/;expires=" + expires + ";";

  if (type == "ba") {
    // set cookie for boost anyone
    document.cookie = "bai=" + encodeURIComponent(baId) + ";domain=" + globalCookieDomain + ";path=/;expires=" + expires + ";";
  }

  if (referrerName) {
    // set cookie for referrer name
    document.cookie = "referrer_name=" + encodeURIComponent(referrerName) + ";domain=" + globalCookieDomain + ";path=/;expires=" + expires + ";";
  }

  const incentiveTtl = getIncentiveTtlValue(incentiveFields?.receiver_incentive_ttl) * 1000;
  const incentiveExpires = new Date(incentiveTtl);
  if (incentiveExpires > new Date()) {
    setIncentiveFieldsCookie(incentiveFields, incentiveExpires);
  } else if (getCookieValue("incentive_fields")) {
    removeIncentiveFieldsCookie();
  }
}

function removeIncentiveFieldsCookie() {
  document.cookie = "incentive_fields=;domain=" + globalCookieDomain + ";path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;";
}

function setIncentiveFieldsCookie(incentiveFields, incentiveExpires) {
  const values = encodeURIComponent(JSON.stringify(incentiveFields));
  document.cookie = "incentive_fields=" + values + ";domain=" + globalCookieDomain + ";path=/;expires=" + incentiveExpires.toGMTString() + ";";
}

/**
 * Returns the text of a module element based on its type
 * @param {HTMLElement} target 
 * @param {string} type 
 * @returns {string}
 */
function getModuleElementText(target, type) {
  type = type.toUpperCase();
  switch (type) {
    case 'LINK':
    case 'BUTTON':
      return target.textContent;
    case 'TOGGLE':
      return (target.textContent.length > 2) ? target.textContent : getComputedStyle(target, ':before').getPropertyValue('content');
    case 'DOTS':
      const beforeContent = getComputedStyle(target, ':before').getPropertyValue('content');
      if (beforeContent && beforeContent != 'none') {
        return beforeContent;
      }
      return target.firstElementChild.getAttribute('aria-label') || target.textContent;
    default:
      return '';
  }
}

/**
 * Fix for https://chime.atlassian.net/browse/OM-28
 * 
 * Assigns each slick slide heading an aria-label attribute
 * that corresponds to "Slide x of y" where x is the current slide number
 */
function assignSlickSlideAriaLabel() {
  /* 
    A page may contain more than one slider. 
    Find each div containing the slick-track class and process separately
  */
  const slickTracks = document.querySelectorAll('div.slick-track');
  
  slickTracks.forEach(slickTrack => {
    /* First, grab the slick slider heading within the slick-track */
    const slickheading = slickTrack.querySelectorAll('div.slick-slide');
    /* Then, count the number of slides. */
    const slicktotal = slickheading.length;
    /* Finally, assign the aria-label */
    slickheading.forEach((_, index) => {
      if (slickheading[index]) {
        slickheading[index].setAttribute('aria-label', `Slide ${index + 1} of ${slicktotal}`);
        slickheading[index].removeAttribute("aria-describedby");
      }
    });
  });
}

/**
 * Get the tags domain
 */
function getTagsDomain() {
  let tagsSubdomain = 'tags';
  const wpEnv = getEnv();

  // Exclude local and prod from the tags subdomain update below
  const envsToExclude = ['local', 'prod'];

  // Update the tags subdomain for devN and qa
  if (!envsToExclude.includes(wpEnv)) {
    tagsSubdomain = `${tagsSubdomain}-${wpEnv}`;
  }

  // Return the tags domain
  return `https://${tagsSubdomain}.chime.com`;
}

/**
 * Fire a postMessage event to the tags.chime.com iframe
 */
function firePostMessageEvent(name) {
  const gtmIframe = document.querySelector('iframe[id="tags_gtm"]');
  const message = 'event';
  const tagsDomain = getTagsDomain();
  const properties = {
    eventName: name,
  };

  gtmIframe?.contentWindow?.postMessage({ message, properties }, tagsDomain);
}

function getNumberFromString(strval) {
  // remove non-numeric characters
  const strrepl = strval.replace(/[\W]+/g, '');
  const val = parseInt(strrepl);
  return isNaN(val) ? 0 : val;
}

function formatNumber(value) {
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    roundingMode: 'floor',
  });

  return formatter.format(getNumberFromString(value));
}


/**
 * Limit number input values to numbers only but allow edit and navigation keys
 * @param {Event} e
 * @returns {string|boolean}
 */
function limitNumberInputValues(e) {
  // check allowed non-numeric keys
  const target = e.target;
  const key = e.key;
  const keys = ["Delete", "Backspace", "Enter", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Tab"];
  
  if (keys.indexOf(key) != -1) {
    // good key
    return true;
  }

  // bad key
  if (isNaN(parseInt(key))) {
    e.preventDefault();
    return false;
  }

  const prevnum = getNumberFromString(target.value);
  const tvalue = prevnum + "" + key;
  const tnum = getNumberFromString(tvalue);
  const tlength = tvalue.length;
  const maxlength = target.maxLength || Infinity;

  // check field length
  if (0 < maxlength && maxlength < Infinity) {
    if (tlength > maxlength) {
      // enforce maxlength on value
      target.value = formatNumber(tvalue.slice(0, maxlength));
      e.preventDefault();
      return 'maxlength';
    }
  }

  const tmin = parseInt(target.min);
  const tmax = parseInt(target.max);

  if (tnum < tmin) {
    return 'minvalue';
  }

  if (tnum > tmax) {
    return 'maxvalue';
  }

  // good number
  e.preventDefault();
  target.value = formatNumber(tvalue);
  return true;
}

/**
 * Converts a simple object to an array of objects with key and value properties
 * and transforms the key into snake_case from camelCase
 * @param {Object} obj The object to convert
 * @returns {Array} An array of objects with key and value properties
 */
function convertObjectToArray(obj) {
  return Object.keys(obj).map(key => ({
    key: key.replace(/([A-Z])/g, '_$1').toLowerCase(),
    value: obj[key]
  }));
}

/**
 * Checks if a given URL is an internal link.
 *
 * This function determines whether the provided URL (`href`) is an internal link
 * by comparing it against the current domain. It returns `true` if the URL matches
 * the current domain, indicating that it is an internal link, and `false` otherwise.
 *
 * @param {string} href - The URL to be checked.
 * @returns {boolean} - Returns `true` if the URL is an internal link, `false` otherwise.
 *
 * @example
 * // Assuming the current domain is 'example.com'
 * isInternalLink('https://example.com/page'); // true
 * isInternalLink('https://external.com/page'); // false
 */
function isInternalLink(href) {
  // get the current domain
  const domain = window.location.hostname.replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&" // $& means the whole matched string
  );
  
  // check if the link is an outbound link
  return href.match(
    new RegExp(`https?:\/\/(.*${domain})`)
  );
}

/**
 * Checks if any class in the class list contains a given partial string.
 *
 * This function takes a DOMTokenList of class names and a partial string,
 * and returns `true` if any class name contains the partial string (case-insensitive),
 * and `false` otherwise.
 *
 * @param {DOMTokenList} classList - The list of class names to be checked.
 * @param {string} partialString - The partial string to search for within the class names.
 * @returns {boolean} - Returns `true` if any class name contains the partial string, `false` otherwise.
 *
 * @example
 * // Assuming an element with class "btn-primary active"
 * const classList = document.querySelector('.btn-primary').classList;
 * matchPartialClassByString(classList, 'primary'); // true
 * matchPartialClassByString(classList, 'inactive'); // false
 */
function matchPartialClassByString(classList, partialString) {
  const list = classList.value.split(" ");
  return list.some((cls) => cls.toLowerCase().includes(partialString));
}
